const STORAGE_KEY = 'lms_org_branding_v1';

const SIGNED_URL_EXPIRY_BUFFER_MS = 30 * 1000;

const parseSignedUrlExpiry = (url) => {
  if (!url || typeof url !== 'string') return null;

  try {
    const parsedUrl = new URL(url);
    const amzDate = parsedUrl.searchParams.get('X-Amz-Date');
    const amzExpires = parsedUrl.searchParams.get('X-Amz-Expires');

    if (amzDate && amzExpires) {
      const match = amzDate.match(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/
      );
      if (!match) return null;

      const [, year, month, day, hour, minute, second] = match;
      const signedAt = Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
      return signedAt + Number(amzExpires) * 1000;
    }

    const legacyExpires = parsedUrl.searchParams.get('Expires');
    if (legacyExpires && /^\d+$/.test(legacyExpires)) {
      return Number(legacyExpires) * 1000;
    }
  } catch {
    return null;
  }

  return null;
};

export function isExpiredSignedMediaUrl(url) {
  const expiresAt = parseSignedUrlExpiry(url);
  if (!expiresAt) return false;
  return Date.now() >= expiresAt - SIGNED_URL_EXPIRY_BUFFER_MS;
}

const normalizeCachedMediaUrl = (url) =>
  isExpiredSignedMediaUrl(url) ? null : url;

/**
 * @returns {{ name: string|null, logo: string|null, favicon: string|null, landing_page_enabled?: boolean, landing_page_url?: string|null } | null}
 */
export function getCachedOrgBranding() {
  if (typeof window === 'undefined') return null;
  const origin = window.location.origin;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.organization_url !== origin) return null;
    return {
      name: parsed.name ?? null,
      logo: normalizeCachedMediaUrl(parsed.logo ?? null),
      favicon: normalizeCachedMediaUrl(parsed.favicon ?? null),
      landing_page_enabled: Boolean(parsed.landing_page_enabled),
      landing_page_url: parsed.landing_page_url ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * @param {{ name?: string|null, logo?: string|null, favicon?: string|null, landing_page_enabled?: boolean, landing_page_url?: string|null }} data
 */
export function setCachedOrgBranding(data) {
  if (typeof window === 'undefined' || !data) return;
  const organization_url = window.location.origin;
  const payload = {
    organization_url,
    name: data.name ?? null,
    logo: data.logo ?? null,
    favicon: data.favicon ?? null,
    landing_page_enabled: Boolean(data.landing_page_enabled),
    landing_page_url: data.landing_page_url ?? null,
    cachedAt: Date.now(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota or private mode — ignore
  }
}

let faviconRequest = 0;
const getOrganizationAppUrl = (path, params = {}) => {
  const hostname = window.location.hostname;
  const apiBase = (
    process.env.REACT_APP_API_URL ||
    (hostname === 'localhost' ? 'http://localhost:4000' : 'https://api.nxgenvarsity.com')
  ).replace(/\/$/, '');
  const url = new URL(`${apiBase}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

export function applyOrgFavicon(faviconUrl, { name = null } = {}) {
  if (!faviconUrl || typeof document === 'undefined' || isExpiredSignedMediaUrl(faviconUrl)) return;
  const request = ++faviconRequest;
  const image = new Image();
  image.onload = () => {
    if (request !== faviconRequest || !image.naturalWidth || !image.naturalHeight) return;
    let links = [...document.querySelectorAll("link[rel='icon']")];
    if (!links.length) {
      const link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
      links = [link];
    }
    // Do not leave another icon pointing at a competing brand, or advertise
    // the fallback size/type for an organization-uploaded image.
    links.forEach((link) => {
      link.href = faviconUrl;
      link.removeAttribute('type');
      link.removeAttribute('sizes');
    });

    const organizationUrl = window.location.origin;
    const manifestHref = getOrganizationAppUrl('/org/auth/organization-manifest.webmanifest', {
      organization_url: organizationUrl,
    });
    const touchIconHref = getOrganizationAppUrl('/org/auth/organization-app-icon', {
      organization_url: organizationUrl,
      size: 192,
    });
    let manifestLink = document.querySelector("link[rel='manifest']");
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }
    manifestLink.href = manifestHref;

    let touchIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (!touchIcon) {
      touchIcon = document.createElement('link');
      touchIcon.rel = 'apple-touch-icon';
      document.head.appendChild(touchIcon);
    }
    touchIcon.href = touchIconHref;
    touchIcon.setAttribute('sizes', '192x192');

    const appTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
    if (appTitle && name) {
      appTitle.setAttribute('content', name);
    }
  };
  // Failed/expired org images leave the usable public fallback in place.
  image.src = faviconUrl;
}
